import { forwardRef, type ComponentProps, type ComponentRef } from 'react';
import { Menu as MuiMenu } from '@mui/material';
import { mergeSlotClassName } from './internal/mergeSlotClassName';

export type RwMenuProps = ComponentProps<typeof MuiMenu>;

/**
 * `.rw-menu` is authored in the DS CSS for the **in-flow** picker dropdown: a panel inside a
 * `position: relative` field wrapper, where `position: absolute; top: calc(100% + 2px); left: 0;
 * width: 100%` correctly means "sit under the trigger, as wide as the trigger".
 *
 * A MUI Menu is not that. Its paper is portaled to `<body>` and positioned by MUI with **inline**
 * top/left, which win over the class — but MUI sets no inline `width`, so `width: 100%` survives and
 * resolves against the viewport. That is what made a floating RwMenu span the full screen width.
 *
 * The class is still wanted for the panel's surface (border, radius, scroll behaviour), so rather
 * than dropping it, the three properties that only make sense in-flow are neutralised here.
 */
const FLOATING_PANEL_RESET = {
  position: 'absolute',
  width: 'auto',
  minWidth: 'max-content',
  top: 'auto',
  left: 'auto'
} as const;

// The box-shadow comes from the theme-wide MuiMenu override in themeOptions.ts —
// this wrapper only wires the .rw-menu floating-panel class onto the paper slot.
export const RwMenu = forwardRef<ComponentRef<typeof MuiMenu>, RwMenuProps>(function RwMenu({ slotProps, ...props }, ref) {
  const paper = mergeSlotClassName(slotProps?.paper, 'rw-menu');
  const paperSx = (paper as { sx?: unknown })?.sx;

  return (
    <MuiMenu
      ref={ref}
      slotProps={{
        ...slotProps,
        paper: {
          ...paper,
          // Caller sx is applied after the reset so a consumer can still pin an explicit width.
          sx: [FLOATING_PANEL_RESET, ...(Array.isArray(paperSx) ? paperSx : [paperSx])]
        }
      }}
      {...props}
    />
  );
});
